import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import { CakeSlice, Heart, Images, MessageCircle, RotateCcw, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { ParticleRain } from '../components/ParticleRain';
import { Sticker } from '../components/Sticker';
import { birthdayConfig, type Hotel, type Tour } from '../config';
import { useBirthdayAudio } from '../hooks/useBirthdayAudio';

type Props = {
  selectedHotel: Hotel | null;
  selectedTour: Tour | null;
  onReplay: () => void;
};

export function FinalSurpriseSection({ selectedHotel, selectedTour, onReplay }: Props) {
  const [wishMade, setWishMade] = useState(false);
  const [loveBurst, setLoveBurst] = useState(false);
  const { playEffect } = useBirthdayAudio();

  const whatsappUrl = useMemo(() => {
    const message = `My birthday choices 💖\n\nDinner hotel: ${selectedHotel?.name || 'Not selected'}\nDecember trip: ${selectedTour?.title || 'Not selected'}\n\nI have completed your birthday surprise!`;
    const cleanNumber = birthdayConfig.sender.whatsappNumber.replace(/\D/g, '');
    return cleanNumber && !cleanNumber.includes('X')
      ? `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
  }, [selectedHotel, selectedTour]);

  const makeWish = () => {
    setWishMade(true);
    playEffect(birthdayConfig.audio.birthdayTrack);
    window.setTimeout(() => {
      confetti({ particleCount: 220, spread: 120, startVelocity: 45, origin: { y: 0.62 }, colors: ['#ff4fa3', '#ffd166', '#a855f7', '#ffffff'] });
    }, 420);
  };

  const celebrateLove = () => {
    setLoveBurst(true);
    confetti({ particleCount: 120, spread: 95, origin: { y: 0.76 }, shapes: ['circle', 'square'], colors: ['#ff4fa3', '#ff8ec7', '#ffd166'] });
    window.setTimeout(() => setLoveBurst(false), 2500);
  };

  return (
    <section id="surprise" className={`section-shell final-section ${wishMade ? 'wish-made' : ''}`}>
      <ParticleRain kind="sparkles" count={wishMade ? 35 : 16} />
      <AnimatePresence>{wishMade && <motion.div className="wish-dimmer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />}</AnimatePresence>
      <div className="page-container final-content">
        <motion.div className="final-copy" initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="eyebrow">{birthdayConfig.copy.final.eyebrow}</span>
          <h2>{birthdayConfig.copy.final.title.split('\n').map((line, index) => <span key={line}>{index > 0 && <br />}{line}</span>)}</h2>
          <p>{birthdayConfig.copy.final.description}</p>
        </motion.div>

        <div className="photo-heart" aria-label="Heart made from six birthday memory photos">
          {birthdayConfig.memories.map((memory, index) => (
            <div key={memory.image} className={`heart-photo heart-photo-${index + 1}`}>
              <ImageWithFallback src={memory.image} alt={memory.title} className="heart-photo-image" loading="lazy" />
            </div>
          ))}
          <span className="heart-center">💖</span>
        </div>

        <div className="cake-stage glass">
          <div className="cake" aria-label="Animated birthday cake">
            <div className="candles">
              {[0, 1, 2, 3, 4].map((candle) => (
                <span key={candle} className="candle"><i className={wishMade ? 'flame out' : 'flame'} /></span>
              ))}
            </div>
            <div className="cake-top">Happy Birthday</div>
            <div className="cake-layer cake-layer-one" />
            <div className="cake-layer cake-layer-two" />
          </div>
          {!wishMade ? (
            <button className="primary-button wish-button" onClick={makeWish}><CakeSlice size={19} /> {birthdayConfig.copy.final.wishButton}</button>
          ) : (
            <motion.div className="wish-message" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <Sparkles size={28} />
              <h3>{birthdayConfig.copy.final.wishMessage}</h3>
              <Sticker src="/stickers/mochi-hug.svg" alt="Mochi cat birthday hug" className="final-sticker" />
            </motion.div>
          )}
        </div>

        <div className="final-actions">
          <button className="secondary-button" onClick={onReplay}><RotateCcw size={18} /> {birthdayConfig.copy.final.replayButton}</button>
          <button className="secondary-button" onClick={() => document.getElementById('memories')?.scrollIntoView({ behavior: 'smooth' })}><Images size={18} /> {birthdayConfig.copy.final.photosButton}</button>
          <a className="secondary-button" href={whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle size={18} /> {birthdayConfig.copy.final.choicesButton}</a>
          <button className="primary-button" onClick={celebrateLove}><Heart size={18} fill="currentColor" /> {birthdayConfig.copy.final.loveButton}</button>
        </div>
        <AnimatePresence>{loveBurst && <motion.div className="final-love-burst" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>💖 💕 💋 💕 💖</motion.div>}</AnimatePresence>
        <div className="final-signature">Made with endless love for {birthdayConfig.birthdayPerson.name}<span>— {birthdayConfig.sender.name}</span></div>
      </div>
    </section>
  );
}
